import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  stripRefPrefix,
  type DevOpsConnection,
  type DevOpsProject,
  type DevOpsPullRequest,
  type DevOpsRepo,
  type DevOpsOverviewClientRef,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import { swallowedFailure } from "../shared/swallowedFind";
import { clientsForProject, type ProjectClientsView } from "../shared/projectClients";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export type RepoSortKey = "name" | "defaultBranch" | "branchCount" | "size";

export type RepoPullRequestSortKey =
  | "title"
  | "author"
  | "branches"
  | "status"
  | "creationDate";

/** Repo sort keys that read best biggest-first on first click. */
const REPO_DESC_FIRST: ReadonlySet<RepoSortKey> = new Set([
  "branchCount",
  "size",
]);

/** PR sort keys that read best newest-first on first click. */
const PR_DESC_FIRST: ReadonlySet<RepoPullRequestSortKey> = new Set([
  "creationDate",
]);

/**
 * Normalise a ServiceStore `find()` response into a plain array of rows.
 * Paginated services return `{ data, total }`; custom non-paginated finds may
 * return the bare array — tolerate both. (Replaces the removed `unwrapList`.)
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * View store for the Repos board (code-side counterpart of the Pipelines and
 * Releases boards).
 *
 * Plain class with `makeAutoObservable` (route ViewStores never extend
 * ViewStore). Backed by registered `@intrasync/stores` ServiceStores
 * (`devops-connections`, `devops-projects`, `devops-repos`). Lists connections,
 * then their live Azure DevOps projects + Git repositories. Connection/project
 * selector shape is identical to PipelinesBoardStore / ReleasesStore. Entirely
 * READ-ONLY — there are no repo write actions.
 *
 * `connections` is a computed view over the `devops-connections` entity cache.
 * `projects`/`repos` are scoped, re-queried Azure proxies read from each
 * `find()` RESPONSE; per-repo detail (`get`) responses are read directly (not
 * via the shared cache, which accumulates across selections).
 *
 * Two enrichments on top of the bare repo list:
 *   - branch counts: loaded in the background per repo via `devops-repos` get
 *     (which attaches `branches`); each repo degrades silently to "—".
 *   - active pull requests: selecting a repo row fetches it again with
 *     `includePullRequests: true` and renders the attached PR panel.
 *
 * WHICH connection/project is the shared `devopsScopeStore`'s fact, not this
 * store's — see PipelinesBoardStore for the rule. Only the repo drill-in
 * (`selectedRepoId`) is board-local.
 */
class ReposStore {
  rootStore: RootStore;

  projects: DevOpsProject[] = [];
  repos: DevOpsRepo[] = [];

  /** The repo whose active pull requests are shown in the PR panel. */
  selectedRepoId: string | null = null;
  pullRequests: DevOpsPullRequest[] = [];

  connectionsLoading = false;
  projectsLoading = false;
  reposLoading = false;
  pullRequestsLoading = false;
  error: string | null = null;

  // Header filter (applied client-side over the loaded repo list).
  searchQuery = "";

  // Column sort for the repos table (client-side over the filtered rows)
  sortKey: RepoSortKey = "name";
  sortDir: SortDir = "asc";

  // Column sort for the expanded pull-requests table (created newest-first)
  prSortKey: RepoPullRequestSortKey = "creationDate";
  prSortDir: SortDir = "desc";

  /**
   * Sequence stamps for the two SCOPED reads — the same discipline as
   * `DevOpsSubscriptionsStore.projectsSeq` and `DevOpsProjectStore.loadSeq`.
   * Project A is chosen, then B. B answers first and paints; A's slower
   * answer must NOT repaint the grid while the shared scope says B, or the
   * page would offer B's project beside A's rows and an action would combine
   * the two. A superseded read writes nothing at all — not the rows, not its
   * error, and not the spinner its `finally` would otherwise clear out from
   * under the read that is still running.
   */
  private projectsSeq = 0;
  private reposSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- ServiceStore accessors ----

  private get connectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  private get projectsService(): ServiceStoreType | null {
    return (this.rootStore.devopsProjectsStore as ServiceStoreType | null) ?? null;
  }

  private get reposService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-repos") ?? this.rootStore.devopsReposStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /** Connection list — computed over the `devops-connections` entity cache. */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  // ---- The shared scope ----

  private get scopeStore(): DevOpsScopeStore | null {
    return (this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined) ?? null;
  }

  get selectedConnectionId(): string | null {
    return this.scopeStore?.connectionId ?? null;
  }

  get selectedProjectId(): string | null {
    return this.scopeStore?.projectId ?? null;
  }

  /** Clients linked to the scoped project (from a loaded overview; never fetched here). */
  get projectClients(): ProjectClientsView {
    return clientsForProject(
      this.rootStore,
      this.selectedConnectionId,
      this.selectedProjectId
    );
  }

  get clientsForCurrentProject(): DevOpsOverviewClientRef[] {
    return this.projectClients.clients;
  }

  // ---- Derived ----

  get selectedConnection(): DevOpsConnection | null {
    if (!this.selectedConnectionId) return null;
    return (
      this.connections.find((c) => c.id === this.selectedConnectionId) || null
    );
  }

  get selectedProject(): DevOpsProject | null {
    if (!this.selectedProjectId) return null;
    return this.projects.find((p) => p.id === this.selectedProjectId) || null;
  }

  get selectedRepo(): DevOpsRepo | null {
    if (!this.selectedRepoId) return null;
    return this.repos.find((r) => r.id === this.selectedRepoId) || null;
  }

  /** Sum of the branch counts loaded so far (background enrichment). */
  get totalBranchCount(): number {
    return this.repos.reduce((sum, r) => sum + (r.branchCount ?? 0), 0);
  }

  get filteredRepos(): DevOpsRepo[] {
    if (!this.searchQuery.trim()) return this.repos;
    const q = this.searchQuery.toLowerCase().trim();
    return this.repos.filter((r) => r.name?.toLowerCase().includes(q));
  }

  /**
   * The rows the repos table renders: name search, then the active column
   * sort. Nulls (e.g. a branch count still loading) sort last either way.
   */
  get sortedRepos(): DevOpsRepo[] {
    const key = this.sortKey;
    const valueOf = (r: DevOpsRepo): SortValue => {
      switch (key) {
        case "name":
          return textValue(r.name);
        case "defaultBranch":
          return textValue(stripRefPrefix(r.defaultBranch));
        case "branchCount":
          return r.branchCount ?? null;
        case "size":
          return r.size ?? null;
      }
    };
    return sortRows(this.filteredRepos, valueOf, this.sortDir);
  }

  /** The expanded PR panel's rows under its own column sort (created desc-first). */
  get sortedPullRequests(): DevOpsPullRequest[] {
    const key = this.prSortKey;
    const valueOf = (pr: DevOpsPullRequest): SortValue => {
      switch (key) {
        case "title":
          return textValue(pr.title);
        case "author":
          return textValue(pr.author);
        case "branches":
          return textValue(stripRefPrefix(pr.sourceRefName));
        case "status":
          return textValue(pr.status);
        case "creationDate":
          return dateValue(pr.creationDate);
      }
    };
    return sortRows(this.pullRequests, valueOf, this.prSortDir);
  }

  // ---- Filter / sort setters ----

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  /** Repos-table header click: toggle / start in the column's natural direction. */
  setSort(key: RepoSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, REPO_DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  /** PR-table header click: toggle / start in the column's natural direction. */
  setPrSort(key: RepoPullRequestSortKey) {
    const next = nextSort(this.prSortKey, this.prSortDir, key, PR_DESC_FIRST);
    this.prSortKey = next.key;
    this.prSortDir = next.dir;
  }

  // ---- Loading ----

  /**
   * Entry point from the route (and from a Project 360 tab). A scope chosen
   * anywhere else is honoured as-is; only an EMPTY scope auto-picks.
   */
  async initialize() {
    const ended = sessionMark(this.rootStore);
    await this.loadConnections();
    // A sign-out during the read leaves `error` null (it is no failure of the
    // next person's), so the `error` check below cannot end the sequence.
    if (ended()) return;
    // A remembered connection that no longer exists is dropped (with its
    // project) so the auto-pick below runs — never after a FAILED load, which
    // `loadConnections` records on `error`. See PipelinesBoardStore.initialize.
    // A failed connections load ends the sequence. There is no list to
    // reconcile against and no connection to read a project for, and going on
    // would replace the CAUSAL error with whatever the doomed project read
    // reports — telling the operator the projects are unavailable when the
    // truth is that HQ could not reach the connections at all.
    if (this.error) return;
    this.scopeStore?.reconcileConnections(this.connections.map((c) => c.id));
    if (this.selectedConnectionId) {
      await this.adoptScope();
    } else if (this.connections.length > 0) {
      await this.selectConnection(this.connections[0].id);
    }
  }

  /**
   * Load this board for a scope set elsewhere. Same rule as
   * PipelinesBoardStore.adoptScope: a project the connection no longer lists
   * is cleared (project only), nothing is guessed in its place, and a failed
   * project read leaves the scope untouched.
   */
  private async adoptScope() {
    const ended = sessionMark(this.rootStore);
    await this.loadProjects();
    // As in initialize: a sign-out leaves no error, and going on would clear
    // the shared scope's project against the list reset() emptied.
    if (ended() || this.error) return;
    const projectId = this.selectedProjectId;
    if (!projectId) {
      runInAction(() => {
        this.selectedRepoId = null;
        this.repos = [];
        this.pullRequests = [];
      });
      return;
    }
    const project = this.projects.find((p) => p.id === projectId);
    if (!project) {
      runInAction(() => {
        this.scopeStore?.setProject(null);
        this.selectedRepoId = null;
        this.repos = [];
        this.pullRequests = [];
      });
      return;
    }
    if (!this.scopeStore?.projectName) {
      this.scopeStore?.setScope({ projectName: project.name });
    }
    await this.loadRepos();
  }

  /**
   * Route onExit. No live feed to tear down here (yet — the plan's finding);
   * what does need dropping is the pull-request drill-in, which is a per-visit
   * exploration, not part of the scope: the next visit should open on the repo
   * list, not on whichever repo was last expanded.
   */
  dispose() {
    this.selectedRepoId = null;
    this.pullRequests = [];
  }

  async loadConnections() {
    this.connectionsLoading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      const result = await this.connectionsService?.find({
        query: { $limit: 200, $sort: { name: 1 } },
      });
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never an org with no connections. It matters twice over here: the
      // cache is empty on a fresh session (`enableStorage: false`), so a
      // silent failure would hand `initialize`'s reconcile an empty list and
      // wipe the remembered scope — the one thing reconcileConnections is
      // documented never to do on a failed load.
      if (result == null) {
        throw swallowedFailure(this.connectionsService, "errorOnFind", "Failed to load connections");
      }
    } catch (err: unknown) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to load connections";
      });

      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.connectionsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope (a new connection clears the project). */
  async selectConnection(connectionId: string | null) {
    runInAction(() => {
      // Invalidate both reads in flight for the scope being left. The loaders
      // below stamp their own sequence when a selection IS made, but clearing
      // one loads nothing — without this bump the previous scope's answer
      // still matches and paints itself onto a board that has moved on.
      this.projectsSeq += 1;
      this.reposSeq += 1;
      this.projectsLoading = false;
      this.reposLoading = false;
      this.scopeStore?.setScope({ connectionId, projectId: null, projectName: null });
      this.selectedRepoId = null;
      this.projects = [];
      this.repos = [];
      this.pullRequests = [];
    });
    if (connectionId) {
      await this.loadProjects();
      // Auto-select the connection's default project (or the first one).
      const conn = this.selectedConnection;
      const defaultProj =
        (conn?.defaultProject &&
          this.projects.find(
            (p) =>
              p.name === conn.defaultProject || p.id === conn.defaultProject
          )) ||
        this.projects[0];
      if (defaultProj) {
        await this.selectProject(defaultProj.id);
      }
    }
  }

  async loadProjects() {
    if (!this.selectedConnectionId) return;
    const seq = ++this.projectsSeq;
    this.projectsLoading = true;
    try {
      // Live read-only proxy to Azure DevOps via the connection (scoped) →
      // read the find() response rows.
      // No `$limit`: `devops-projects.find` reads only `connectionId` and
      // calls Azure's core.getProjects(), returning whatever page Azure gives
      // back. A `$limit` here was inert — it looked like an enforced cap and
      // was not one. Azure's own paging is the real bound; if an org ever has
      // more projects than one page, the picker needs a search, not a number.
      const result = await this.projectsService?.find({
        query: { connectionId: this.selectedConnectionId },
      });
      // A null result is a swallowed failure, not an empty org (see
      // shared/swallowedFind.ts) — adoptScope must not clear the project on it.
      if (seq !== this.projectsSeq) return; // a newer scope superseded this
      if (result == null) {
        throw swallowedFailure(this.projectsService, "errorOnFind", "Failed to load projects");
      }
      runInAction(() => {
        this.projects = rowsOf<DevOpsProject>(
          result as unknown as { data?: DevOpsProject[] } | DevOpsProject[],
        );
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a scope
      // this board has left.
      if (seq !== this.projectsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load projects";
        }
      });

      console.error("Failed to load DevOps projects:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.projectsSeq) this.projectsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope, name included. */
  async selectProject(projectId: string | null) {
    runInAction(() => {
      // Invalidate the board read in flight for the project being left.
      this.reposSeq += 1;
      this.reposLoading = false;
      this.scopeStore?.setProject(
        projectId,
        this.projects.find((p) => p.id === projectId)?.name ?? null
      );
      this.selectedRepoId = null;
      this.repos = [];
      this.pullRequests = [];
    });
    if (projectId) {
      await this.loadRepos();
    }
  }

  async loadRepos() {
    if (!this.selectedConnectionId || !this.selectedProjectId) return;
    const seq = ++this.reposSeq;
    this.reposLoading = true;
    this.error = null;
    try {
      // Live read-only proxy — returns a bare array of repo records (scoped) →
      // read the find() response rows.
      // No `$limit`: `devops-repos.find` reads connectionId/projectId (plus an
      // optional repoId for the branch drill-in) and ignores the rest, so the
      // old `$limit: 500` was inert. Nor is one needed — Azure's
      // git.getRepositories(project) returns a project's repositories in full,
      // with no continuation token, so this list IS complete and needs no
      // "first N" caveat (unlike the pipelines/releases/work-item boards).
      const result = await this.reposService?.find({
        query: {
          connectionId: this.selectedConnectionId,
          projectId: this.selectedProjectId,
        },
      });
      if (seq !== this.reposSeq) return; // a newer project superseded this
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never a project with no repositories — and must not empty a populated
      // grid with nothing saying the read failed.
      if (result == null) {
        throw swallowedFailure(this.reposService, "errorOnFind", "Failed to load repositories");
      }
      runInAction(() => {
        this.repos = rowsOf<DevOpsRepo>(
          result as unknown as { data?: DevOpsRepo[] } | DevOpsRepo[],
        );
      });
      // Background enrichment — never blocks the grid render.
      void this.loadBranchCounts();
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a project
      // this board has left.
      if (seq !== this.reposSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load repositories";
        }
      });

      console.error("Failed to load DevOps repositories:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.reposSeq) this.reposLoading = false;
      });
    }
  }

  /**
   * Batch-fetch branch counts for the loaded repos. The list read does not
   * attach branches (only the per-repo get does), so each repo is re-read via
   * `devops-repos` get — the API's per-org rate limiter smooths the burst.
   * DEGRADES SILENTLY per repo: a failed lookup just leaves its count blank.
   */
  async loadBranchCounts() {
    const connectionId = this.selectedConnectionId;
    const projectId = this.selectedProjectId;
    if (!connectionId || !projectId) return;
    // The wrapper swallows a sign-out into undefined; its answer is nobody's now.
    const ended = sessionMark(this.rootStore);
    await Promise.all(
      this.repos.map(async (repo) => {
        try {
          const detail = (await this.reposService?.get(repo.id, {
            query: { connectionId, projectId },
          })) as unknown as DevOpsRepo | undefined;
          if (ended()) return;
          runInAction(() => {
            // Guard against a selection change racing the in-flight reads.
            const target = this.repos.find((r) => r.id === repo.id);
            if (target) {
              target.branchCount = detail?.branches?.length ?? 0;
            }
          });
        } catch {
          // leave branchCount undefined — the grid shows "—"
        }
      })
    );
  }

  /** Row click — toggle the PR panel onto this repo and load its active PRs. */
  async selectRepo(repoId: string | null) {
    if (repoId === this.selectedRepoId) {
      // Toggle off.
      runInAction(() => {
        this.selectedRepoId = null;
        this.pullRequests = [];
      });
      return;
    }
    runInAction(() => {
      this.selectedRepoId = repoId;
      this.pullRequests = [];
    });
    if (repoId) {
      await this.loadPullRequests();
    }
  }

  /**
   * Load the selected repo's ACTIVE pull requests — `devops-repos` get with
   * `includePullRequests: true` attaches them to the record.
   */
  async loadPullRequests() {
    const connectionId = this.selectedConnectionId;
    const projectId = this.selectedProjectId;
    const repoId = this.selectedRepoId;
    if (!connectionId || !projectId || !repoId) return;
    this.pullRequestsLoading = true;
    try {
      const detail = (await this.reposService?.get(repoId, {
        query: {
          connectionId,
          projectId,
          includePullRequests: true,
        },
      })) as unknown as DevOpsRepo | undefined;
      runInAction(() => {
        // Ignore a stale response after the selection moved on.
        if (this.selectedRepoId === repoId) {
          this.pullRequests = detail?.pullRequests ?? [];
        }
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load pull requests";
        }
      });

      console.error("Failed to load DevOps pull requests:", err);
    } finally {
      runInAction(() => {
        this.pullRequestsLoading = false;
      });
    }
  }

  /** Header refresh — re-pull repos (and the open PR panel) for the selection. */
  async reload() {
    if (this.selectedConnectionId && this.selectedProjectId) {
      await this.loadRepos();
      if (this.selectedRepoId) {
        await this.loadPullRequests();
      }
    } else {
      await this.initialize();
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.projects = [];
    this.repos = [];
    this.selectedRepoId = null;
    this.pullRequests = [];
    this.connectionsLoading = false;
    this.projectsLoading = false;
    this.reposLoading = false;
    this.pullRequestsLoading = false;
    this.error = null;
    this.searchQuery = "";
    this.sortKey = "name";
    this.sortDir = "asc";
    this.prSortKey = "creationDate";
    this.prSortDir = "desc";
    this.projectsSeq += 1;
    this.reposSeq += 1;
  }
}

export default ReposStore;
